import { ILogger } from '@sparkware/uc-sdk-core';
import Container, { Service } from 'typedi';
import DeferredObject from '../../../Modules/Utils/DeferredObject';
import { LoggerProvider } from '../logger';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';
import { DefaultPostLoginActionState, PostLoginStateKey } from './enums';
import { IPostLoginActionsConfigurationModel } from './models/IPostLoginActionsConfigurationModel';
import { IPostLoginActionOptions, IPostLoginActionsState } from './models/IPostLoginActionsState';

@Service()
export class PostLoginStorage {
    private _actionsDO: DeferredObject<Array<IPostLoginActionsConfigurationModel>>;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;

    private readonly _logger: ILogger;

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('PostLoginStorage');
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._actionsDO = new DeferredObject({ storeResolved: true });
    }

    public getState = (): IPostLoginActionsState => {
        const state: IPostLoginActionsState = JSON.parse(
            this._localSimpleStoreService.get(PostLoginStateKey) ||
                JSON.stringify(DefaultPostLoginActionState),
        );

        this._logger.debug('getState: current state is:', state);

        return state;
    };

    public updateState = (state: IPostLoginActionsState): IPostLoginActionsState => {
        this._localSimpleStoreService.set(
            PostLoginStateKey,
            JSON.stringify(state || DefaultPostLoginActionState),
        );
        this._logger.debug(
            `updateState: executed with the following state: ${JSON.stringify(
                state || DefaultPostLoginActionState,
            )}`,
        );

        return state;
    };

    public storeActions = (actions: Array<IPostLoginActionsConfigurationModel>) => {
        this._actionsDO.resolve(actions);
    };

    public getActions = async (): Promise<Array<IPostLoginActionsConfigurationModel>> => {
        return this._actionsDO.promise;
    };

    public storeActionsOptions = (options?: IPostLoginActionOptions): void => {
        this._logger.debug(
            `storeActionsOptions: started with the following options: ${JSON.stringify(
                options || 'no options specified',
            )}`,
        );
        if (options) {
            const currentState: IPostLoginActionsState = this.getState();
            currentState.actionsOptions = options;
            this.updateState(currentState);

            this._logger.debug(`storeActionsOptions: options saved.`);
        }
    };

    public updateActionOptions = (actionName, options: any) => {
        const state = this.getState();
        if (!state.actionsOptions) {
            state.actionsOptions = {};
        }
        state.actionsOptions[actionName] = options;
        this.updateState(state);
        this._logger.debug(
            `updateActionOptions: actionName=${actionName}, updating action options to: `,
            options || 'options removed',
        );
    };

    public getCurrentPostLoginAction = (): IPostLoginActionsConfigurationModel | null => {
        try {
            const state = this.getState();

            if (state?.lastActionInExecution && !state.completed) {
                return state.lastActionInExecution;
            }
        } catch (error) {
            this._logger.error(
                'getCurrentPostLoginAction: error while getting the current action:',
                error,
            );
        }

        return null;
    };
}
